import * as React from 'react';
import Link from 'components/Link';
import {
  RiLightbulbFlashLine,
  //RiBarChartHorizontalLine
} from 'react-icons/ri';
import { AiFillStar, AiOutlineStar } from 'react-icons/ai';
import { BsBarChartSteps } from 'react-icons/bs';
import { FaDownload } from 'react-icons/fa';
import { MdInfoOutline } from 'react-icons/md';
import { TbCalendarDollar } from 'react-icons/tb';
import { Switch, Transition } from '@headlessui/react';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useEarningsData } from 'hooks/useEarningsQuery';
import { Timezone_Location_Enum } from 'types/generated/client';
import convertTimestampToTime from 'utils/shared/time/convertTimestampToTime';
import formatShortMonthDate from 'utils/shared/time/formatShortMonthDate';
import abbreviateNumber from 'utils/shared/number/abbreviateNumber';
// import { getSectorShortName } from 'utils/shared/trades/sectors';
import { TICKER_FLOW_PAGE } from 'constants/pages';
import SectionTitle from 'components/SectionTitle';
import PulseLoader from 'components/Loading/Pulse';
import {
  SortDirection,
  EquityPrintOverview,
  SignaturePrintStatus,
  Sentiment,
  MobileLayout,
} from 'utils/shared/trades/types';
import getSignaturePrintStatus from 'utils/shared/trades/getSignaturePrintStatus';
import getSignaturePrintSentiment from 'utils/shared/trades/getSignaturePrintSentiment';
import { getIsGoldenDarkPool } from 'utils/shared/trades/getIsGoldenDarkPool';
import { FINRA_EXCHANGE_ID, LARGE_TRADE_MINIMUM } from 'constants/darkpool';
import {
  TableWrapper,
  TableCell,
  TableCellNoPad,
  TableHeader,
  TableBody,
  HeaderCell,
  MutedText,
  HeaderCellWrapper,
  HeaderIcon,
  IconSortDesc,
  IconSortAsc,
  WatchListIcon,
} from 'components/Table';
import DarkPoolLevels from 'components/DarkPoolLevels';
import PermissionedBlock from 'components/PermissionedBlock';
import TooltipComponent from 'components/Tooltip';
import { getEarningsTooltip } from 'utils/shared/earnings';
import { trackEvent } from 'services/client/analytics';
import { DOWNLOAD_DARK_POOL } from 'services/client/analytics/events';
import { convertToCSV, downloadCSV } from 'utils/client/csvUtils';
import classNames from 'styles/utils/classNames';
import {
  Props,
  DarkPoolTableHeaderProps,
  DarkPoolTableRowProps,
  DARK_POOL_TABLE_HEADERS,
  DEFAULT_FLOW_KEY,
  DATE_COL,
} from './props';

import {
  HighVolumeIndicator,
  MobileHighVolumeIndicator,
  MobileDarkPoolCard,
  MobileDarkPoolCardBackground,
  MobileDarkPoolTicker,
  DarkPoolRow,
  HighlightedPrice,
  SentimentDot,
  //  HeaderButton
} from './styles';

const TITLE_NAMES = DARK_POOL_TABLE_HEADERS;
const ROW_HEIGHT = 38.19;
const MOBILE_CARD_HEIGHT = 52;

type DisplayedDarkPoolTrade = DarkPoolTableRowProps['trade'];

const formatDarkPoolData = (
  trades: DisplayedDarkPoolTrade[],
  { isWallstreetTimezone, includeDate }: { isWallstreetTimezone: boolean; includeDate?: boolean },
) => {
  return trades.map((trade) => {
    const { time, timeSubscript } = convertTimestampToTime({
      timestamp: trade.timestampIso,
      isWallstreetTimezone,
    });
    const isLitPool = trade.exchangeId !== FINRA_EXCHANGE_ID;

    return {
      ...(includeDate ? { Date: trade.date } : {}),
      Time: `${time} ${timeSubscript}`,
      Ticker: trade.ticker,
      Price: `$${trade.price?.toFixed(2)}`,
      Size: trade.size?.toLocaleString(),
      Amount: `$${abbreviateNumber(trade.amount)}`,
      Pool: isLitPool ? 'Lit' : 'Dark',
    };
  });
};

const handleDownload = (
  trades: DisplayedDarkPoolTrade[],
  options: { isWallstreetTimezone: boolean; includeDate?: boolean },
): void => {
  const csv = convertToCSV(formatDarkPoolData(trades, options));
  downloadCSV(csv, 'Dark Pool Prints');
};

const Header: React.FC<React.PropsWithChildren<DarkPoolTableHeaderProps>> = React.memo(
  ({
    flowSortKey,
    setFlowSortKey,
    flowSortDirection,
    setFlowSortDirection,
    isWatchListOnly,
    setIsWatchListOnly,
    hasWatchListItems,
    includeDate,
    right,
    setShowInfo,
    setIsWatchListModalOpen,
    mobileLayout,
    isEarningsOnly,
    setIsEarningsOnly,
    hideHeader,
    hideColumnHeader,
    hideLevelsColumn,
    hideEarningsToggle = true,
    title = 'Dark Pool Prints',
    onDownload,
  }) => {
    const columns = includeDate ? [DATE_COL, ...TITLE_NAMES] : TITLE_NAMES;

    const handleOpenWatchListModal = () => {
      setIsWatchListModalOpen?.(true);
    };
    return (
      <TableHeader
        style={
          hideColumnHeader && !hideHeader
            ? { paddingTop: '1.25rem', paddingBottom: '0.5rem' }
            : undefined
        }
      >
        {!hideHeader && (
          <div className="mb-0 flex min-w-0 flex-row items-center justify-between gap-2">
            <div className="flex w-full min-w-0 flex-wrap items-center gap-2">
              {!!right && (
                <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:hidden">
                  <RiLightbulbFlashLine
                    aria-hidden="true"
                    className="h-3.5 w-3.5 flex-shrink-0 text-graph-green"
                  />
                  {right}
                </div>
              )}
              <SectionTitle
                className={classNames(
                  'min-w-0 flex-1 flex-wrap gap-2',
                  !!right && 'hidden sm:flex',
                )}
                Icon={RiLightbulbFlashLine}
                title={title}
                style={{ marginBottom: 0 }}
                right={right}
              />
              {!!setIsWatchListOnly && (
                <Switch.Group as="div" className="flex flex-shrink-0 items-center">
                  <Switch
                    checked={isWatchListOnly}
                    onChange={setIsWatchListOnly}
                    className="group relative inline-flex h-3 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                  >
                    <span
                      aria-hidden="true"
                      className={`${isWatchListOnly ? 'bg-yellow-400' : 'bg-gray-600'}
                      pointer-events-none absolute mx-auto h-2 w-5 rounded-full transition-colors duration-200 ease-in-out
                    `}
                    />
                    <span
                      aria-hidden="true"
                      className={`${isWatchListOnly ? 'translate-x-3' : 'translate-x-0'} ${
                        isWatchListOnly ? 'bg-white' : 'bg-gray-300'
                      } pointer-events-none absolute left-0 inline-block h-3 w-3 transform rounded-full border border-gray-400 shadow ring-0 transition-transform duration-200 ease-in-out`}
                    />
                  </Switch>
                  <div className="ml-2 text-xs">
                    <TooltipComponent title="Update your watchlist" position="left">
                      <button
                        type="button"
                        onClick={handleOpenWatchListModal}
                        className="flex cursor-pointer items-center whitespace-nowrap rounded-[4px] border border-gray-300 bg-gray-50 px-0.5 py-0.5 text-xs font-semibold text-yellow-500 shadow-sm transition-colors hover:bg-gray-100 active:bg-gray-200 sm:px-1 sm:py-1"
                      >
                        <AiFillStar
                          aria-hidden="true"
                          className={`h-3 w-3 sm:h-auto sm:w-auto ${
                            isWatchListOnly ? 'text-yellow-400' : 'text-gray-600'
                          }`}
                        />
                      </button>
                    </TooltipComponent>
                  </div>
                  {isWatchListOnly && !hasWatchListItems && (
                    <Switch.Label as="span" className="ml-2 leading-none" style={{ lineHeight: 0 }}>
                      <span className="text-xs font-medium leading-none text-yellow-400">
                        Add items to your watchlist to appear in the feed
                      </span>
                    </Switch.Label>
                  )}
                </Switch.Group>
              )}
              {!hideEarningsToggle && (
                <Switch.Group as="div" className="flex flex-shrink-0 items-center">
                  <Switch
                    checked={isEarningsOnly || false}
                    onChange={setIsEarningsOnly}
                    className="group relative inline-flex h-3 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                  >
                    <span
                      aria-hidden="true"
                      className={`${isEarningsOnly ? 'bg-graph-purple' : 'bg-gray-600'}
                  pointer-events-none absolute mx-auto h-2 w-5 rounded-full transition-colors duration-200 ease-in-out
                `}
                    />
                    <span
                      aria-hidden="true"
                      className={`${isEarningsOnly ? 'translate-x-3' : 'translate-x-0'} ${
                        isEarningsOnly ? 'bg-white' : 'bg-gray-300'
                      } pointer-events-none absolute left-0 inline-block h-3 w-3 transform rounded-full border border-gray-400 shadow ring-0 transition-transform duration-200 ease-in-out`}
                    />
                  </Switch>
                  <div
                    className={`ml-0.5 text-xs ${
                      isEarningsOnly ? 'text-yellow-400' : 'text-gray-600'
                    }`}
                  >
                    <div
                      className={`rounded-[4px] px-0.5 py-0.5 transition-all duration-300 sm:px-1 sm:py-1 ${
                        isEarningsOnly ? ' text-graph-purple shadow-lg' : ' text-slate-400'
                      }`}
                    >
                      <TooltipComponent
                        title={
                          mobileLayout === MobileLayout.Cards
                            ? 'Show tickers with upcoming earnings'
                            : 'Upcoming earnings'
                        }
                        position={mobileLayout === MobileLayout.Cards ? 'left' : 'left'}
                      >
                        <TbCalendarDollar aria-hidden="true" className="h-5 w-5" />
                      </TooltipComponent>
                    </div>
                  </div>
                </Switch.Group>
              )}
            </div>
            {!!onDownload && (
              <TooltipComponent title="Download CSV" position="left">
                <button
                  type="button"
                  aria-label="Download dark pool prints"
                  onClick={onDownload}
                  className="flex h-6 flex-shrink-0 items-center justify-center rounded-[4px] bg-palette-border px-2 text-[.65rem] shadow-sm"
                >
                  <FaDownload />
                </button>
              </TooltipComponent>
            )}
          </div>
        )}
        {!hideColumnHeader && (
          <DarkPoolRow
            isHeader
            removePad
            includeDate={includeDate}
            hideLevelsColumn={hideLevelsColumn}
          >
            {columns.map((t, i) => {
              const isActiveSort =
                t.sortKey === flowSortKey && flowSortDirection !== SortDirection.None;
              const isFinalRow = i === columns.length - 1;

              if (isFinalRow && includeDate) {
                return null;
              }

              if (isFinalRow && hideLevelsColumn) {
                return null;
              }

              return (
                <HeaderCellWrapper
                  key={t.name}
                  removePad={i + 1 === TITLE_NAMES.length}
                  isActiveSort={isActiveSort}
                  onClick={() => {
                    if (isFinalRow) {
                      setShowInfo((showInfo: boolean) => !showInfo);
                      return;
                    }

                    if (flowSortKey !== t.sortKey) {
                      setFlowSortKey(t.sortKey);
                      setFlowSortDirection(SortDirection.Desc);
                    } else if (
                      flowSortKey === t.sortKey &&
                      flowSortDirection === SortDirection.Desc
                    ) {
                      setFlowSortDirection(SortDirection.Asc);
                    } else if (
                      flowSortKey === t.sortKey &&
                      flowSortDirection === SortDirection.Asc
                    ) {
                      setFlowSortKey(DEFAULT_FLOW_KEY);
                      setFlowSortDirection(SortDirection.None);
                    }
                  }}
                >
                  <HeaderCell>
                    <span
                      className="cell-name flex items-center"
                      onMouseOver={() => {
                        if (isFinalRow) {
                          setShowInfo(true);
                        }
                      }}
                      onMouseOut={() => {
                        if (isFinalRow) {
                          setShowInfo(false);
                        }
                      }}
                    >
                      {t.name}{' '}
                      {isFinalRow && (
                        <div className="relative flex items-center">
                          <MdInfoOutline className="ml-1 text-xs text-graph-purple" />
                        </div>
                      )}
                    </span>
                    {!isFinalRow && isActiveSort && flowSortDirection === SortDirection.Desc && (
                      <HeaderIcon>
                        <IconSortDesc />
                      </HeaderIcon>
                    )}
                    {!isFinalRow && isActiveSort && flowSortDirection === SortDirection.Asc && (
                      <HeaderIcon>
                        <IconSortAsc />
                      </HeaderIcon>
                    )}
                  </HeaderCell>
                </HeaderCellWrapper>
              );
            })}
          </DarkPoolRow>
        )}
      </TableHeader>
    );
  },
);

const Row: React.FC<React.PropsWithChildren<DarkPoolTableRowProps>> = ({
  trade,
  watchListTickers,
  signaturePrintCount,
  includeDate,
  trackRowAsAnimated,
  hasBeenAnimated,
  setDarkPoolLevelTicker,
  showInfo,
  handleBookmark,
  hideLevelsColumn,
  hasEarnings,
  getEarningsData,
  getDaysToEarnings,
  tickerDetailsMap,
}) => {
  const { user: currentUser } = useCurrentUser();
  const timezoneLocation = currentUser?.timezoneLocation || Timezone_Location_Enum.Current;
  const isWallstreetTimezone = timezoneLocation === Timezone_Location_Enum.Wallstreet;
  const isLitPool = trade.exchangeId !== FINRA_EXCHANGE_ID;

  const isWatchListTrade = !!trade.ticker && watchListTickers.includes(trade.ticker);
  const { time, timeSubscript } = convertTimestampToTime({
    timestamp: trade.timestampIso,
    isWallstreetTimezone,
  });

  const tickerHasEarnings = hasEarnings(trade.ticker);
  const earningsData = getEarningsData(trade.ticker);
  const daysToEarnings = getDaysToEarnings(trade.ticker);
  const tooltipTitle = tickerHasEarnings
    ? getEarningsTooltip(earningsData, daysToEarnings)
    : 'No upcoming earnings';

  const onBookmarkClick = () => {
    if (handleBookmark && trade.ticker) {
      handleBookmark(trade.ticker);
    }
  };

  React.useEffect(() => {
    return () => {
      if (trackRowAsAnimated && !hasBeenAnimated) {
        trackRowAsAnimated();
      }
    };
  }, [hasBeenAnimated, trackRowAsAnimated]);

  return (
    <DarkPoolRow
      tableBodyRow
      isAnimated={!hasBeenAnimated}
      signaturePrintStatus={trade.signaturePrintStatus}
      includeDate={includeDate}
      style={{ height: `${ROW_HEIGHT}px` }}
      isLitPool={isLitPool}
      hideLevelsColumn={hideLevelsColumn}
    >
      {includeDate && (
        <TableCell>
          <MutedText>{trade.date}</MutedText>
        </TableCell>
      )}
      <TableCell>
        <MutedText>
          {time} {timeSubscript}
        </MutedText>
      </TableCell>
      <TableCell className="relative">
        <WatchListIcon className="absolute z-[1000] text-yellow-400">
          <button
            className={`-m-1 flex-shrink-0 cursor-pointer rounded p-1 transition-all duration-200 hover:scale-[1.75] active:scale-110 ${
              isWatchListTrade
                ? 'text-yellow-400 hover:text-yellow-300'
                : 'text-gray-400 hover:text-yellow-400'
            }`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onBookmarkClick();
            }}
          >
            {isWatchListTrade ? <AiFillStar /> : <AiOutlineStar />}
          </button>
        </WatchListIcon>
        <div className="relative inline-flex items-center">
          <Link href={`${TICKER_FLOW_PAGE}/${trade.ticker}`}>
            <TooltipComponent
              title={
                trade?.tickerDetails?.name || tickerDetailsMap?.[trade.ticker || '']?.name || ''
              }
              position="right"
            >
              {trade.ticker}
            </TooltipComponent>
          </Link>
          {tickerHasEarnings && (
            <TooltipComponent title={tooltipTitle} position="right">
              <sup className="-top-2 cursor-pointer">
                <TbCalendarDollar
                  aria-hidden="true"
                  className="h-3 w-3 rounded-sm text-graph-purple"
                />
              </sup>
            </TooltipComponent>
          )}
          {trade.signaturePrintNumber > 0 && (
            <span className="ml-1 text-gray-400" style={{ fontSize: '.6rem' }}>
              {signaturePrintCount - trade.signaturePrintNumber + 1}
            </span>
          )}
        </div>
      </TableCell>
      {/* <TableCell>{trade.sector}</TableCell> */}
      <TableCell>
        <span className="flex items-center">
          ${trade.price.toFixed(2)}{' '}
          {trade.signaturePrintSentiment !== Sentiment.None && (
            <SentimentDot sentiment={trade.signaturePrintSentiment} />
          )}
        </span>
      </TableCell>
      <TableCell>
        <HighVolumeIndicator isHighVolume={getIsGoldenDarkPool(trade.size, trade.avgVolume)}>
          {trade.size?.toLocaleString()}
        </HighVolumeIndicator>
      </TableCell>
      <TableCell>
        <HighlightedPrice isLargePrice={trade.amount >= LARGE_TRADE_MINIMUM}>
          ${abbreviateNumber(trade.amount)}
        </HighlightedPrice>
      </TableCell>
      {!includeDate && !hideLevelsColumn && (
        <TableCellNoPad>
          {!isLitPool && (
            <button
              onClick={() => setDarkPoolLevelTicker(trade.ticker || '')}
              style={{ marginLeft: '-6px' }}
              className={classNames(
                showInfo ? 'bg-button-color' : 'bg-transparent',
                'flex items-center rounded border border-button-color px-2 py-1 transition-colors duration-150 hover:bg-button-color',
              )}
            >
              <BsBarChartSteps className="mr-1 h-2 w-2" /> View
            </button>
          )}
        </TableCellNoPad>
      )}
    </DarkPoolRow>
  );
};

const MobileCard: React.FC<React.PropsWithChildren<DarkPoolTableRowProps>> = ({
  trade,
  watchListTickers,
  signaturePrintCount,
  setDarkPoolLevelTicker,
  showInfo,
  handleBookmark,
  hideLevelsColumn,
  hasEarnings,
  getEarningsData,
  getDaysToEarnings,
  tickerDetailsMap,
}) => {
  const { user: currentUser } = useCurrentUser();
  const timezoneLocation = currentUser?.timezoneLocation || Timezone_Location_Enum.Current;
  const isWallstreetTimezone = timezoneLocation === Timezone_Location_Enum.Wallstreet;
  const { time, timeSubscript } = convertTimestampToTime({
    timestamp: trade.timestampIso,
    isWallstreetTimezone,
  });
  const formattedTime = `${time} ${timeSubscript}`;
  const formattedTradeDate = formatShortMonthDate(trade.date);
  const isWatchListTrade = !!trade.ticker && watchListTickers.includes(trade.ticker);
  const tickerHasEarnings = hasEarnings(trade.ticker);
  const earningsData = getEarningsData(trade.ticker);
  const daysToEarnings = getDaysToEarnings(trade.ticker);
  const tooltipTitle = tickerHasEarnings
    ? getEarningsTooltip(earningsData, daysToEarnings)
    : 'No upcoming earnings';
  const isLitPool = trade.exchangeId !== FINRA_EXCHANGE_ID;
  const isHighVolume = getIsGoldenDarkPool(trade.size, trade.avgVolume);
  const hasSignatureSentiment = trade.signaturePrintSentiment !== Sentiment.None;
  const showLevelsButton = !isLitPool && !hideLevelsColumn;
  const signaturePrintDisplayNumber =
    trade.signaturePrintNumber > 0 ? signaturePrintCount - trade.signaturePrintNumber + 1 : null;
  const signatureLabel =
    trade.signaturePrintStatus === SignaturePrintStatus.Buy
      ? 'Buy'
      : trade.signaturePrintStatus === SignaturePrintStatus.Sell
      ? 'Sell'
      : isLitPool
      ? 'Lit'
      : 'Dark';

  return (
    <div className="h-[52px] px-2 pb-1">
      <MobileDarkPoolCard className="h-full overflow-hidden rounded border border-palette-border text-xs leading-none">
        <MobileDarkPoolCardBackground
          className={classNames(
            'grid h-full min-w-0 items-center px-2 py-1.5',
            'grid-cols-[minmax(92px,auto)_minmax(0,1fr)_auto_auto] gap-x-1.5',
          )}
          $signaturePrintStatus={trade.signaturePrintStatus}
          $isLitPool={isLitPool}
        >
          <div className="flex h-full min-w-[92px] flex-col justify-center gap-1">
            <MutedText className="truncate whitespace-nowrap text-[0.6rem] leading-none">
              {formattedTime}
              <span className="px-1 text-gray-600">·</span>
              {formattedTradeDate}
            </MutedText>
            <div className="flex min-w-0 items-center gap-2">
              <div className="relative min-w-[46px] flex-shrink-0 self-center">
                <Link href={`${TICKER_FLOW_PAGE}/${trade.ticker}`}>
                  <TooltipComponent
                    title={
                      trade?.tickerDetails?.name ||
                      tickerDetailsMap?.[trade.ticker || '']?.name ||
                      ''
                    }
                    position="right"
                  >
                    <MobileDarkPoolTicker className="inline-flex items-center justify-center text-white">
                      {trade.ticker}
                    </MobileDarkPoolTicker>
                  </TooltipComponent>
                </Link>
                {!!handleBookmark && (
                  <button
                    className={`absolute -right-1 -top-1 z-10 flex items-center justify-center text-[0.65rem] leading-none transition-colors ${
                      isWatchListTrade
                        ? 'text-yellow-400 hover:text-yellow-300'
                        : 'text-gray-400 hover:text-yellow-400'
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleBookmark(trade.ticker || '');
                    }}
                    aria-label={isWatchListTrade ? 'Remove from watchlist' : 'Add to watchlist'}
                  >
                    {isWatchListTrade ? <AiFillStar /> : <AiOutlineStar />}
                  </button>
                )}
              </div>
              <span className="flex flex-shrink-0 items-center justify-center rounded-sm border border-white-15 px-1 py-0.5 text-[0.5rem] font-semibold uppercase leading-none text-gray-300">
                {signatureLabel}
                {signaturePrintDisplayNumber !== null && (
                  <span className="ml-1 text-[0.48rem] leading-none text-gray-500">
                    #{signaturePrintDisplayNumber}
                  </span>
                )}
              </span>
              {tickerHasEarnings && (
                <TooltipComponent title={tooltipTitle} position="right">
                  <TbCalendarDollar
                    aria-hidden="true"
                    className="h-3.5 w-3.5 flex-shrink-0 rounded-sm text-graph-purple"
                  />
                </TooltipComponent>
              )}
            </div>
          </div>
          <div className="flex h-full min-w-0 items-center justify-center px-1">
            <HighlightedPrice
              className="min-w-0 truncate text-center text-[0.95rem] font-bold leading-none"
              isLargePrice={trade.amount >= LARGE_TRADE_MINIMUM}
            >
              ${abbreviateNumber(trade.amount)}
            </HighlightedPrice>
          </div>
          <div className="flex min-w-0 flex-col items-end justify-center gap-1 text-[0.62rem] leading-none text-gray-300">
            <div className="flex min-w-0 max-w-full items-center justify-end gap-1">
              <MobileHighVolumeIndicator
                className="block min-w-0 max-w-full truncate"
                $isHighVolume={isHighVolume}
              >
                {trade.size?.toLocaleString()}
              </MobileHighVolumeIndicator>
              <span className="text-gray-500">@</span>
            </div>
            <div className="flex min-w-0 items-center justify-end gap-1">
              <span className="min-w-0 truncate">${trade.price.toFixed(2)}</span>
              {hasSignatureSentiment && (
                <SentimentDot
                  className="flex-shrink-0"
                  sentiment={trade.signaturePrintSentiment}
                  style={{ marginLeft: 0 }}
                />
              )}
            </div>
          </div>
          <div
            className={classNames(
              'ml-1 flex h-full items-stretch justify-end',
              showLevelsButton ? 'w-6' : 'w-0',
            )}
          >
            {showLevelsButton && (
              <button
                type="button"
                aria-label={`View ${trade.ticker || ''} dark pool levels`}
                onClick={() => setDarkPoolLevelTicker(trade.ticker || '')}
                className={classNames(
                  showInfo ? 'bg-button-color' : 'bg-transparent',
                  'flex h-7 w-full items-center justify-center self-center rounded-sm border border-button-color text-white transition-colors duration-150 hover:bg-button-color',
                )}
              >
                <BsBarChartSteps className="h-2.5 w-2.5" />
              </button>
            )}
          </div>
        </MobileDarkPoolCardBackground>
      </MobileDarkPoolCard>
    </div>
  );
};

const DarkPoolTable: React.FC<React.PropsWithChildren<Props>> = ({
  watchListTickers,
  isWatchListOnly,
  setIsWatchListOnly,
  hasWatchListItems,
  searchValue,
  trades = [],
  hasAccess,
  loading,
  error,
  includeDate,
  right,
  permissionTitle,
  permissionDescription,
  setIsWatchListModalOpen,
  handleBookmark,
  earningsMap,
  hideHeader,
  hideLevelsColumn,
  hideEarningsToggle = true,
  title,
  tickerDetailsMap,
}) => {
  const searchTerm = (searchValue || '').trim().toUpperCase();
  const [flowSortKey, setFlowSortKey] = React.useState(DEFAULT_FLOW_KEY);
  const [flowSortDirection, setFlowSortDirection] = React.useState(SortDirection.None);
  const [hasLoadedTrades, setHasLoadedTrades] = React.useState(false);
  const animatedRowsRef = React.useRef<Record<string, boolean>>({});
  const [darkPoolLevelTicker, setDarkPoolLevelTicker] = React.useState('');
  const [showInfo, setShowInfo] = React.useState(false);
  const [mobileLayout, setMobileLayout] = React.useState(MobileLayout.Cards);
  const [isEarningsOnly, setIsEarningsOnly] = React.useState(false);
  const { user: currentUser } = useCurrentUser();
  const isWallstreetTimezone =
    (currentUser?.timezoneLocation || Timezone_Location_Enum.Current) ===
    Timezone_Location_Enum.Wallstreet;

  const { hasEarnings, getEarningsData, getDaysToEarnings } = useEarningsData(
    earningsMap?.earnings,
  );

  React.useEffect(() => {
    if (!hasLoadedTrades && trades?.length) {
      const rows: Record<string, boolean> = {};
      trades.forEach((t) => {
        rows[t.id] = true;
      });
      setHasLoadedTrades(true);
      animatedRowsRef.current = rows;
    }
  }, [trades, hasLoadedTrades]);

  // NOTE: This assumes they come in sorted by most recent first
  const equityPrintOverview = React.useMemo(
    () =>
      trades.reduce((acc, trade) => {
        if (!!trade.ticker && !acc[trade.ticker]) {
          acc[trade.ticker] = {
            open: trade.open,
            high: trade.high,
            low: trade.low,
            lastPrice: trade.lastTradePrice,
            avgPrice: trade.avgPrice,
          };
        }

        return acc;
      }, {} as Record<string, EquityPrintOverview>),
    [trades],
  );

  const { displayedTrades, signaturePrintCount } = React.useMemo(() => {
    const signaturePrintSummary: Record<string, number> = {};
    let sortKey = DEFAULT_FLOW_KEY;

    if (
      !flowSortKey ||
      flowSortKey === DEFAULT_FLOW_KEY ||
      flowSortDirection === SortDirection.None
    ) {
      sortKey = DEFAULT_FLOW_KEY;
    } else {
      sortKey = flowSortKey;
    }

    const filteredTrades = trades
      .map((t) => {
        const signaturePrintStatus = getSignaturePrintStatus(
          t,
          equityPrintOverview[t.ticker || ''],
        );
        let signaturePrintNumber = 0;

        if (signaturePrintStatus !== SignaturePrintStatus.None) {
          if (!signaturePrintSummary[t.ticker || '']) {
            signaturePrintSummary[t.ticker || ''] = 0;
          }
          signaturePrintSummary[t.ticker || '']++;
          signaturePrintNumber = signaturePrintSummary[t.ticker || ''];
        }

        return {
          ...t,
          signaturePrintStatus: signaturePrintStatus,
          signaturePrintSentiment: getSignaturePrintSentiment({
            price: t.price,
            lastPrice: equityPrintOverview[t.ticker || ''].lastPrice,
            isSignatureEtfTicker: t.isSignatureEtfTicker || false,
          }),
          // sector: getSectorShortName(t?.tickerDetails?.sector || ''),
          signaturePrintNumber: signaturePrintNumber || 0,
        };
      })
      .filter((item) => {
        const passesWatchListTest = isWatchListOnly
          ? watchListTickers.includes(item.ticker || '')
          : true;
        const passesSearchTest = searchTerm ? item.ticker?.includes(searchTerm) : true;
        const passesEarningsTest = isEarningsOnly ? hasEarnings(item.ticker || '') : true;
        return passesWatchListTest && passesSearchTest && passesEarningsTest;
      });

    const sortedTrades = filteredTrades.sort((a, b) => {
      let first = flowSortDirection === SortDirection.Asc ? a : b;
      let last = flowSortDirection === SortDirection.Asc ? b : a;

      // @ts-ignore
      if (first[sortKey] === last[sortKey]) {
        return (first.size || 0) - (last.size || 0);
      }

      // @ts-ignore
      if (first[sortKey] < last[sortKey]) {
        return -1;
      }
      // @ts-ignore
      if (first[sortKey] > last[sortKey]) {
        return 1;
      }
      return 0;
    });

    return { displayedTrades: sortedTrades, signaturePrintCount: signaturePrintSummary };
  }, [
    trades,
    equityPrintOverview,
    isWatchListOnly,
    watchListTickers,
    searchTerm,
    flowSortKey,
    flowSortDirection,
    isEarningsOnly,
    hasEarnings,
  ]);

  const hasDownloadableData = displayedTrades.length > 0;
  const onDownload = React.useCallback(() => {
    trackEvent(DOWNLOAD_DARK_POOL, { hasAccess });
    if (!hasAccess) {
      return;
    }
    handleDownload(displayedTrades, { isWallstreetTimezone, includeDate });
  }, [hasAccess, displayedTrades, isWallstreetTimezone, includeDate]);

  const RenderRow = React.useCallback(
    ({ index, style }: any) => {
      const trade = displayedTrades[index];

      return (
        <div style={style}>
          <Row
            key={trade.id}
            includeDate={includeDate}
            trade={trade}
            watchListTickers={watchListTickers}
            signaturePrintCount={signaturePrintCount[trade.ticker || ''] || 0}
            hasBeenAnimated={animatedRowsRef.current[trade.id]}
            setDarkPoolLevelTicker={setDarkPoolLevelTicker}
            trackRowAsAnimated={() => {
              animatedRowsRef.current[trade.id] = true;
            }}
            showInfo={showInfo}
            handleBookmark={handleBookmark}
            hideLevelsColumn={hideLevelsColumn}
            hasEarnings={hasEarnings}
            getEarningsData={getEarningsData}
            getDaysToEarnings={getDaysToEarnings}
            tickerDetailsMap={tickerDetailsMap}
          />
        </div>
      );
    },
    [
      displayedTrades,
      watchListTickers,
      setDarkPoolLevelTicker,
      showInfo,
      hideLevelsColumn,
      hasEarnings,
      getEarningsData,
      getDaysToEarnings,
      tickerDetailsMap,
    ],
  );

  const RenderMobileCard = React.useCallback(
    ({ index, style }: any) => {
      const trade = displayedTrades[index];

      return (
        <div style={style}>
          <MobileCard
            key={trade.id}
            trade={trade}
            watchListTickers={watchListTickers}
            signaturePrintCount={signaturePrintCount[trade.ticker || ''] || 0}
            hasBeenAnimated={animatedRowsRef.current[trade.id]}
            setDarkPoolLevelTicker={setDarkPoolLevelTicker}
            trackRowAsAnimated={() => {
              animatedRowsRef.current[trade.id] = true;
            }}
            showInfo={showInfo}
            handleBookmark={handleBookmark}
            hideLevelsColumn={hideLevelsColumn}
            hasEarnings={hasEarnings}
            getEarningsData={getEarningsData}
            getDaysToEarnings={getDaysToEarnings}
            tickerDetailsMap={tickerDetailsMap}
          />
        </div>
      );
    },
    [
      displayedTrades,
      watchListTickers,
      signaturePrintCount,
      setDarkPoolLevelTicker,
      showInfo,
      handleBookmark,
      hideLevelsColumn,
      hasEarnings,
      getEarningsData,
      getDaysToEarnings,
      tickerDetailsMap,
    ],
  );

  return (
    <>
      <div className="hidden h-full min-w-0 sm:block">
        <TableWrapper className="relative h-full w-full min-w-0 max-w-full flex-auto flex-col overflow-x-auto overflow-y-hidden">
          <Header
            includeDate={includeDate}
            flowSortKey={flowSortKey}
            setFlowSortKey={setFlowSortKey}
            flowSortDirection={flowSortDirection}
            setFlowSortDirection={setFlowSortDirection}
            isWatchListOnly={isWatchListOnly}
            setIsWatchListOnly={setIsWatchListOnly}
            hasWatchListItems={hasWatchListItems}
            right={right}
            setShowInfo={setShowInfo}
            setIsWatchListModalOpen={setIsWatchListModalOpen}
            mobileLayout={mobileLayout}
            setMobileLayout={setMobileLayout}
            isEarningsOnly={isEarningsOnly}
            setIsEarningsOnly={setIsEarningsOnly}
            hideHeader={hideHeader}
            hideLevelsColumn={hideLevelsColumn}
            hideEarningsToggle={hideEarningsToggle}
            title={title}
            onDownload={hasDownloadableData ? onDownload : undefined}
            // isSoundEnabled={isSoundEnabled}
            // setIsSoundEnabled={setIsSoundEnabled}
          />
          <TableBody animateRows className="relative h-full w-full">
            {loading && (!trades || trades?.length === 0) ? (
              <div className="flex h-full items-center justify-center">
                <PulseLoader />
              </div>
            ) : !hasAccess ? (
              <PermissionedBlock title={permissionTitle} description={permissionDescription} />
            ) : !!error && (!trades || trades?.length === 0) ? (
              <div className="flex h-full items-center justify-center text-gray-400">&nbsp;</div>
            ) : (
              <div className="h-full w-full overflow-hidden">
                <AutoSizer>
                  {({ width, height }) => (
                    <FixedSizeList
                      height={height}
                      itemCount={displayedTrades.length}
                      itemSize={ROW_HEIGHT}
                      width={width}
                    >
                      {RenderRow}
                    </FixedSizeList>
                  )}
                </AutoSizer>
              </div>
            )}
          </TableBody>
          <Transition
            show={showInfo}
            enter="transition-opacity duration-75"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="absolute bottom-0 left-0 flex w-full items-center justify-center bg-darkpool p-6 text-center text-sm text-white">
              Tracking the price level that dark pool prints are trading over time helps identify
              support and resistance levels. The more volume that was transacted at that price, the
              stronger the support level is likely to be.
            </div>
          </Transition>
        </TableWrapper>
      </div>
      <div className="h-full min-w-0 sm:hidden">
        <TableWrapper className="relative h-full w-full min-w-0 max-w-full flex-auto flex-col overflow-hidden">
          <Header
            includeDate={includeDate}
            flowSortKey={flowSortKey}
            setFlowSortKey={setFlowSortKey}
            flowSortDirection={flowSortDirection}
            setFlowSortDirection={setFlowSortDirection}
            isWatchListOnly={isWatchListOnly}
            setIsWatchListOnly={setIsWatchListOnly}
            hasWatchListItems={hasWatchListItems}
            right={right}
            setShowInfo={setShowInfo}
            setIsWatchListModalOpen={setIsWatchListModalOpen}
            mobileLayout={mobileLayout}
            setMobileLayout={setMobileLayout}
            isEarningsOnly={isEarningsOnly}
            setIsEarningsOnly={setIsEarningsOnly}
            hideHeader={hideHeader}
            hideColumnHeader
            hideLevelsColumn={hideLevelsColumn}
            hideEarningsToggle={hideEarningsToggle}
            title={title}
            onDownload={hasDownloadableData ? onDownload : undefined}
          />
          <TableBody animateRows className="relative h-full w-full overflow-x-hidden">
            {loading && (!trades || trades?.length === 0) ? (
              <div className="flex h-full items-center justify-center">
                <PulseLoader />
              </div>
            ) : !hasAccess ? (
              <PermissionedBlock title={permissionTitle} description={permissionDescription} />
            ) : !!error && (!trades || trades?.length === 0) ? (
              <div className="flex h-full items-center justify-center text-gray-400">&nbsp;</div>
            ) : (
              <div className="h-full w-full overflow-hidden">
                <AutoSizer>
                  {({ width, height }) => (
                    <FixedSizeList
                      height={height}
                      itemCount={displayedTrades.length}
                      itemSize={MOBILE_CARD_HEIGHT}
                      width={width}
                    >
                      {RenderMobileCard}
                    </FixedSizeList>
                  )}
                </AutoSizer>
              </div>
            )}
          </TableBody>
        </TableWrapper>
      </div>
      {!!darkPoolLevelTicker && (
        <DarkPoolLevels ticker={darkPoolLevelTicker} onClose={() => setDarkPoolLevelTicker('')} />
      )}
    </>
  );
};

export default DarkPoolTable;
